import { useId, useLayoutEffect, useRef } from 'react'

interface ConfirmDialogProps {
  open: boolean
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  onConfirm(): void
  onCancel(): void
}

function supportsNativeModalDialog(): boolean {
  return (
    typeof HTMLDialogElement !== 'undefined' &&
    typeof HTMLDialogElement.prototype.showModal === 'function' &&
    typeof HTMLDialogElement.prototype.close === 'function'
  )
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = '确认',
  cancelLabel = '取消',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const fallbackRef = useRef<HTMLDivElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const cancelHandlerRef = useRef(onCancel)
  const nativeDialog = supportsNativeModalDialog()

  useLayoutEffect(() => {
    cancelHandlerRef.current = onCancel
  }, [onCancel])

  useLayoutEffect(() => {
    if (!open) return

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    const dialog = dialogRef.current
    if (nativeDialog && dialog !== null && !dialog.open) {
      try {
        dialog.showModal()
      } catch {
        dialog.setAttribute('open', '')
      }
    }

    cancelRef.current?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || nativeDialog) return
      event.preventDefault()
      cancelHandlerRef.current()
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (dialog !== null && dialog.open) {
        try {
          dialog.close()
        } catch {
          dialog.removeAttribute('open')
        }
      }
      previousFocusRef.current?.focus()
      previousFocusRef.current = null
    }
  }, [nativeDialog, open])

  if (!open) return null

  const content = (
    <>
      <h2 id={titleId}>{title}</h2>
      <p id={descriptionId}>{description}</p>
      <div className="confirm-dialog__actions">
        <button ref={cancelRef} type="button" onClick={onCancel}>
          {cancelLabel}
        </button>
        <button className="danger-button" type="button" onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </>
  )

  if (nativeDialog) {
    return (
      <dialog
        ref={dialogRef}
        className="confirm-dialog"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-modal="true"
        onCancel={(event) => {
          event.preventDefault()
          onCancel()
        }}
      >
        {content}
      </dialog>
    )
  }

  return (
    <div className="dialog-backdrop">
      <div
        ref={fallbackRef}
        className="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        {content}
      </div>
    </div>
  )
}
